import { css } from '@emotion/react'
import { HeadProps, PageProps, graphql } from 'gatsby'
import { Fragment } from 'react'

import { Seo } from '@/features/common/'
import {
  BodySection,
  ContactSection,
  DescriptiveLinksSection,
  GoFurtherSection,
  HighlightSection,
  IntroSection,
  LeadershipSection,
  MatriculationsSection,
  PageHero,
  ThumbnailLinksSection,
} from '@/features/page-sections'

const InteriorPage = ({
  data: { datoCmsInteriorPage: page },
}: PageProps<Queries.InteriorPageQuery>): JSX.Element => {
  let bgIndex = -1
  let imgIndex = -1
  const styles = {
    bodySection: css`
      &:nth-of-type(${page?.bodySections?.length}) {
        padding-bottom: var(--row-108);
      }
    `,
  }
  return (
    <Fragment>
      <PageHero
        heading={page?.pageHeading}
        heroImage={page?.heroImage}
      />

      <IntroSection
        body={page?.intro?.body}
        heading={
          page?.intro?.heading?.__typename === 'DatoCmsIntroHeading'
            ? page?.intro?.heading.heading
            : undefined
        }
        quote={
          page?.intro?.heading?.__typename === 'DatoCmsQuote'
            ? page?.intro.heading
            : undefined
        }
      />

      <section>
        {page?.bodySections?.map(block => {
          bgIndex += 1
          switch (block?.__typename) {
            case 'DatoCmsBodySection': {
              return (
                <BodySection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsBodySectionWithImage': {
              imgIndex += 1
              return (
                <BodySection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  imagePosition={imgIndex % 2 ? 'LEFT' : 'RIGHT'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsDescriptiveLinksSection': {
              return (
                <DescriptiveLinksSection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsHighlightMultimediaSection': {
              imgIndex += 1
              return (
                <HighlightSection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  textAlignment={imgIndex % 2 ? 'RIGHT' : 'LEFT'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsLeadershipSection': {
              return (
                <LeadershipSection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsThumbnailLinksSection': {
              return (
                <ThumbnailLinksSection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  css={styles.bodySection}
                />
              )
            }
            case 'DatoCmsMatriculationsSection': {
              return (
                <MatriculationsSection
                  data={block}
                  key={block.id}
                  bgColor={bgIndex % 2 ? 'WHITE' : 'GRAY'}
                  css={styles.bodySection}
                />
              )
            }
          }
        })}
      </section>
      <ContactSection data={page?.contact?.[0]} />
      {page?.goFurther?.map(section => (
        <GoFurtherSection
          data={section}
          key={section?.id}
        />
      ))}
    </Fragment>
  )
}

export const Head = ({
  data,
}: HeadProps<Queries.InteriorPageQuery>) => (
  <Seo seoMetaTags={data?.datoCmsInteriorPage?.seoMetaTags} />
)

export const data = graphql`
  query InteriorPage($id: String!) {
    datoCmsInteriorPage(id: { eq: $id }) {
      pageHeading
      heroImage {
        ...PageHero
      }
      intro {
        ...IntroSection
      }
      bodySections {
        ... on DatoCmsBodySection {
          ...BodySection
        }
        ... on DatoCmsBodySectionWithImage {
          ...BodySectionWithImage
        }
        ... on DatoCmsDescriptiveLinksSection {
          ...DescriptiveLinksSection
        }
        ... on DatoCmsHighlightMultimediaSection {
          ...HighlightMultimediaSection
        }
        ... on DatoCmsLeadershipSection {
          ...LeadershipSection
        }
        ... on DatoCmsThumbnailLinksSection {
          ...ThumbnailLinksSection
        }
        ... on DatoCmsMatriculationsSection {
          ...MatriculationsSection
        }
      }
      contact {
        ...ContactSection
      }
      goFurther {
        ...GoFurtherSection
      }
      seoMetaTags {
        tags
      }
    }
  }
`

export default InteriorPage
